/**
 * TeamModelsSection: the models a team can call through the gateway,
 * configured by staff without gateway or database access. Each row is a
 * route in the team's gateway config, shown with where it sends traffic
 * (a catalog model, or a private upstream and the endpoint behind it),
 * its aliases, its fallback chain and the Baseten ids the uptime probe
 * reads. Add, edit and delete open the same dialogs the team's own Models
 * page uses, with a staff scope, so the two paths cannot drift. The star
 * makes a row the team's default, the model its nightly evals score, as
 * Make default does on the team's own model page.
 *
 * A team gets its gateway team on its first model, so a team without one
 * has an empty list and the Add button still works.
 */

import { useState } from 'react'
import { Pencil, Plus, Star, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { useListTeamModelsApiInternalTeamsTeamIdModelsGet } from '@/api/generated/internal/internal'
import type { TeamModelAdminRow } from '@/api/generated/model'
import { DefaultModelBadge } from '@/components/models/DefaultModelBadge'
import { DeleteModelDialog } from '@/components/models/DeleteModelDialog'
import { ModelFormDialog } from '@/components/models/ModelFormDialog'
import { RowAction } from '@/components/settings/RowAction'
import { SettingsList, SettingsListRow } from '@/components/settings/SettingsList'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock, SettingsSection } from '@/components/settings/SettingsSection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSetDefaultModel, type ModelScope } from '@/hooks/useModels'

import type { TeamConfigSectionProps } from './sections'
import { selectedModel, servesLabel, type OpenDialog } from './teamModels'

export function TeamModelsSection({ section, team }: TeamConfigSectionProps): React.JSX.Element {
  const scope: ModelScope = { kind: 'staff', teamId: team.id }
  const query = useListTeamModelsApiInternalTeamsTeamIdModelsGet(team.id)
  const [dialog, setDialog] = useState<OpenDialog>(null)
  const setDefault = useSetDefaultModel(scope)
  const data = query.data?.status === 200 ? query.data.data : undefined
  // The dialogs render the model as the list holds it on this render, so
  // an alias edit's refetch reaches the open editor. See selectedModel.
  const selected = selectedModel(dialog, data?.models ?? [])

  function closeDialog(open: boolean): void {
    if (!open) setDialog(null)
  }

  return (
    <div>
      <SettingsPageHeader
        section={section}
        action={
          <Button
            onClick={() => {
              setDialog({ kind: 'create' })
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add model
          </Button>
        }
      />
      {query.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : query.isError ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(query.error) ?? 'Failed to load the models.'}
          </p>
        </SettingsBlock>
      ) : data ? (
        <>
          <SettingsSection
            title="Gateway team"
            description="The team's slug on the gateway. Every model id starts with it."
          >
            {data.gateway_team_id === null ? (
              <p className="text-sm text-muted-foreground">
                This team has no gateway team yet. Adding a model creates one.
              </p>
            ) : (
              <p className="font-mono text-sm text-foreground">{data.gateway_team_id}</p>
            )}
          </SettingsSection>
          <SettingsSection
            title="Models"
            description="Each row is a route in the team's gateway config. Edit opens the same dialog the team uses."
          >
            {data.models.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No models yet. Add one to register the team's first gateway route.
              </p>
            ) : (
              <SettingsList>
                {data.models.map((row) => (
                  <ModelRow
                    key={row.model.id}
                    row={row}
                    isSettingDefault={setDefault.isPending}
                    onMakeDefault={() => {
                      setDefault.mutate(
                        { modelId: row.model.id },
                        {
                          onSuccess: (model) => {
                            toast.success(`${model.display_name} is now the default`)
                          },
                        },
                      )
                    }}
                    onEdit={() => {
                      setDialog({ kind: 'edit', modelId: row.model.id })
                    }}
                    onDelete={() => {
                      setDialog({ kind: 'delete', modelId: row.model.id })
                    }}
                  />
                ))}
              </SettingsList>
            )}
          </SettingsSection>
        </>
      ) : null}

      <ModelFormDialog
        mode="create"
        scope={scope}
        open={dialog?.kind === 'create'}
        onOpenChange={closeDialog}
        onSaved={(model) => {
          toast.success(`${model.display_name} added`)
        }}
      />
      {dialog?.kind === 'edit' && selected !== undefined && (
        <ModelFormDialog
          mode="edit"
          model={selected}
          scope={scope}
          open
          onOpenChange={closeDialog}
          onSaved={() => {
            toast.success('Model saved')
          }}
        />
      )}
      {dialog?.kind === 'delete' && selected !== undefined && (
        <DeleteModelDialog
          model={selected}
          scope={scope}
          open
          onOpenChange={closeDialog}
          onDeleted={() => {
            toast.success('Model deleted')
          }}
        />
      )}
    </div>
  )
}

function ModelRow({
  row,
  isSettingDefault,
  onMakeDefault,
  onEdit,
  onDelete,
}: {
  row: TeamModelAdminRow
  isSettingDefault: boolean
  onMakeDefault: () => void
  onEdit: () => void
  onDelete: () => void
}): React.JSX.Element {
  const { model } = row
  const endpoint = row.forwards_to?.endpoint_url ?? null
  return (
    <SettingsListRow className="items-start py-3">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground">{model.display_name}</span>
          <span className="font-mono text-xs text-muted-foreground">{model.gateway_model_id}</span>
          {model.is_default && <DefaultModelBadge />}
          {/* An alias is a slug, so it keeps its case like every other id here. */}
          {model.aliases.map((alias) => (
            <Badge key={alias} variant="outline" className="normal-case tracking-normal">
              {alias}
            </Badge>
          ))}
        </div>
        <p
          className={
            row.route === null
              ? 'font-mono text-xs text-destructive'
              : 'font-mono text-xs text-muted-foreground'
          }
        >
          {servesLabel(row)}
        </p>
        {endpoint !== null && (
          <p className="break-all font-mono text-xs text-muted-foreground">{endpoint}</p>
        )}
        {row.baseten_model_id !== null && (
          <p className="font-mono text-xs text-muted-foreground">
            Baseten model {row.baseten_model_id}
          </p>
        )}
        {row.baseten_deployment_id !== null && (
          <p className="font-mono text-xs text-muted-foreground">
            Baseten deployment {row.baseten_deployment_id}
          </p>
        )}
        {row.fallbacks.length > 0 && (
          <p className="font-mono text-xs text-muted-foreground">
            Falls back to {row.fallbacks.join(', ')}
          </p>
        )}
      </div>
      <span className="flex items-center gap-0.5">
        {!model.is_default && (
          <RowAction
            label={`Make ${model.display_name} the default`}
            icon={Star}
            disabled={isSettingDefault}
            onClick={onMakeDefault}
          />
        )}
        <RowAction label={`Edit ${model.display_name}`} icon={Pencil} onClick={onEdit} />
        <RowAction
          label={`Delete ${model.display_name}`}
          icon={Trash2}
          destructive
          onClick={onDelete}
        />
      </span>
    </SettingsListRow>
  )
}
